import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Container } from '@/components/Container/Container';
import { CtaBanner } from '@/components/CtaBanner/CtaBanner';
import { IconDoc } from '@/components/Icons/IconDoc';
import { IconDownload } from '@/components/Icons/IconDownload';
import { PublicPage } from '@/components/Page/PublicPage';
import SkeletonLoader from '@/components/SkeletonLoader/SkeletonLoader';
import { TagGroupCard } from '@/components/TagGroupCard/TagGroupCard';
import { CONSTANTS } from '@/constants';
import { useProcessState } from '@/hooks/useProcessState/useProcessState';
import { SEARCH_DIRECTORIES_WITH_FILES_QUERY } from '@/hooks/useSearchDirectoriesWithFiles/useSearchDirectoriesWithFiles';
import { ESize } from '@/models/enums';
import { IDirectory, useSearchDirectories } from '@netfront/ekardo-content-library';
import { Button } from '@netfront/ui-library';
import { NextPage } from 'next';
import { useEffect, useState } from 'react';

interface ITagGroup {
  id: number;
  name: string;
  sort: number;
  items: IDirectory[];
}

const FactSheetsNextPage: NextPage = () => {
  const { start, complete, fail, isCompleted, isLoading } = useProcessState();
  const [factSheets, setFactSheets] = useState<IDirectory[]>([]);

  const { handleSearchDirectories } = useSearchDirectories({
    onCompleted: ({ directories }) => {
      setFactSheets(directories);
      complete();
    },
    onError: () => {
      fail();
    },
    query: SEARCH_DIRECTORIES_WITH_FILES_QUERY,
  });

  useEffect(() => {
    start();
    handleSearchDirectories({
      directoryTypeId: CONSTANTS.DIRECTORY.FACT_SHEETS.ID,
      projectId: String(process.env.REACT_APP_PROJECT_ID),
    });
  }, []);

  const featuredItem = factSheets[0] ?? null;

  const tagGroups = factSheets
    .reduce<ITagGroup[]>((groups, item) => {
      if (!item.linkedTags || item.linkedTags.length === 0) return groups;
      const tag = item.linkedTags[0];
      const existing = groups.find(g => g.id === tag.id);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.push({ id: tag.id, name: tag.name, sort: tag.sort, items: [item] });
      }
      return groups;
    }, [])
    .sort((a, b) => a.sort - b.sort);

  return (
    <PublicPage
      meta={{
        seoDescription: 'Download DAWN fact sheets and resources.',
        seoTitle: 'Fact sheets',
      }}
      title="Fact sheets"
    >
      <Container additionalClassNames="py-12" size={ESize.MD}>
        <div className="mb-2">
          <Breadcrumbs items={[{ content: <span>Fact sheets</span>, key: 'fact-sheets' }]} />
        </div>

        <div className="mb-8">
          <p className="text-uppercase text-xs font-bold color-grey-600 mb-2" style={{ letterSpacing: '0.05em' }}>Free · Download &amp; Share</p>
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="flex-1">
              <h1 className="color-black mb-2">Plain-language reading, made for diabetes.</h1>
              <p className="color-grey-600 mb-0">
                Take one to your next appointment. Read one with a family member. Print one for the fridge.{' '}
                They&apos;re short on purpose.
              </p>
            </div>
            {isCompleted && factSheets.length > 0 && (
              <p className="text-sm color-grey-600 shrink-0 mb-0">
                {factSheets.length} sheet{factSheets.length !== 1 ? 's' : ''} · adapted from eCLiPSE
              </p>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col gap-4">
            <SkeletonLoader additionalClassNames="h-64 rounded-lg" />
            <div className="grid gap-4 lg:grid-cols-3">
              <SkeletonLoader additionalClassNames="h-48 rounded-lg" />
              <SkeletonLoader additionalClassNames="h-48 rounded-lg" />
              <SkeletonLoader additionalClassNames="h-48 rounded-lg" />
            </div>
          </div>
        )}

        {isCompleted && factSheets.length > 0 && (
          <>
            {featuredItem && (
              <div className="shadow rounded-lg overflow-hidden mb-10 flex flex-col lg:flex-row">
                <div className="w-full lg:w-2/5 bg-grey-100 flex items-center justify-center min-h-48 p-8">
                  {featuredItem.asset?.presignedUrl ? (
                    <img
                      alt={featuredItem.title}
                      className="w-full h-full object-cover"
                      src={featuredItem.asset.presignedUrl}
                    />
                  ) : (
                    <p className="color-grey-500 text-sm text-center mb-0" style={{ fontFamily: 'monospace' }}>
                      cover · {featuredItem.title.toLowerCase()}
                    </p>
                  )}
                </div>

                <div className="p-8 flex flex-col justify-center flex-1">
                  <span className="inline-block bg-primary color-white text-sm font-bold px-3 py-1 rounded-full mb-4 self-start">
                    Start here
                  </span>
                  <h2 className="mb-2">{featuredItem.title}</h2>
                  {featuredItem.subTitle && (
                    <p className="color-grey-600 text-sm mb-3" style={{ fontFamily: 'monospace' }}>
                      {featuredItem.subTitle}
                    </p>
                  )}
                  {featuredItem.description && (
                    <p className="color-grey-600 mb-6">{featuredItem.description}</p>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    {(() => {
                      const filePresignedUrl = (featuredItem as any).files?.find((f: any) => f?.asset?.presignedUrl)?.asset?.presignedUrl;
                      const downloadUrl = filePresignedUrl ?? featuredItem.asset?.presignedUrl;
                      return downloadUrl ? (
                        <Button
                          linkButton={{ id: 'download-featured', url: downloadUrl, target: '_blank' }}
                          text="Download PDF"
                          variant="accent"
                        />
                      ) : null;
                    })()}
                    {featuredItem.url && (
                      <Button
                        linkButton={{ id: 'read-featured', url: featuredItem.url, target: '_blank' }}
                        text="Read online"
                        variant="secondary"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {tagGroups.length > 0 && (
              <div>
                <h2 className="mb-6">Browse by topic</h2>
                <div className="grid gap-4 lg:grid-cols-3">
                  {tagGroups.map(({ id, name, items }) => (
                    <TagGroupCard key={id} name={name} items={items} />
                  ))}
                </div>
              </div>
            )}

            {tagGroups.length === 0 && (
              <ul className="list-style-none m-0 flex flex-col gap-3">
                {factSheets.map((item) => {
                  const { asset, description, id, title, url } = item;
                  const filePresignedUrl = (item as any).files?.find((f: any) => f?.asset?.presignedUrl)?.asset?.presignedUrl;
                  const downloadUrl = filePresignedUrl ?? asset?.presignedUrl ?? url ?? '#';
                  return (
                  <li key={id} className="m-0">
                    <a
                      aria-label={`Download ${title}`}
                      className="flex items-center gap-4 shadow rounded-lg p-5 no-underline color-black transition-150 hover:shadow-md"
                      href={downloadUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <IconDoc />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold mb-0">{title}</p>
                        {description && <p className="color-grey-600 text-sm mb-0">{description}</p>}
                      </div>
                      <IconDownload />
                    </a>
                  </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {isCompleted && factSheets.length === 0 && (
          <p>No fact sheets found.</p>
        )}
      </Container>

      <CtaBanner
        label="For Clinicians"
        heading="Looking for clinician-facing fact sheets?"
        description="Versions written for diabetes educators and primary care."
        button={{ id: 'clinician-resources', url: '/for-clinicians', text: 'Clinician resources →' }}
      />
    </PublicPage>
  );
};

export default FactSheetsNextPage;
